"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { PerpAssetContext } from "../../shared/types";
import { useHyperliquidWsConnection } from "./use-ws-connection";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const THROTTLE_DELAY = 500;

function queryKey(coin: string) {
  return ["hyperliquid-ws-markPrice", coin] as const;
}

interface ActiveAssetCtxMessage {
  channel?: string;
  data?: {
    coin?: string;
    ctx?: PerpAssetContext;
  };
}

/**
 * Hook that subscribes to Hyperliquid's `activeAssetCtx` WebSocket channel
 * for a single coin's real-time mark price.
 *
 * Uses the per-coin `activeAssetCtx` subscription which returns the full
 * asset context (funding, OI, volume, prices) for the specified coin.
 *
 * @see https://hyperliquid.gitbook.io/hyperliquid-docs/for-developers/api/websocket/subscriptions
 */
export const useHyperliquidMarkPriceWS = ({
  coin: rawCoin,
  enabled = true,
}: {
  /** Coin identifier — accepts "BTC", "BTC-PERP", etc. The "-PERP" suffix is stripped automatically. */
  coin: string | undefined;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const lastUpdateTimeRef = useRef(0);
  const throttleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // activeAssetCtx expects the base asset name (e.g. "BTC"), not "BTC-PERP"
  const coin = rawCoin?.replace(/-PERP$/, "") || undefined;
  const isEnabled = enabled && !!coin;
  const key = queryKey(coin ?? "");

  const { data: markPrice } = useQuery<number | null>({
    queryKey: key,
    queryFn: () => null,
    enabled: false, // No fetching — data comes from WS via setQueryData
    staleTime: Infinity,
  });

  const subscription = useMemo(() => (coin ? { type: "activeAssetCtx", coin } : undefined), [coin]);

  const onMessage = useCallback(
    (msg: unknown) => {
      const message = msg as ActiveAssetCtxMessage;
      if (message.channel !== "activeAssetCtx" || !message.data?.ctx || message.data.coin !== coin) return;

      const { markPx } = message.data.ctx;
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      const doUpdate = () => {
        lastUpdateTimeRef.current = Date.now();
        queryClient.setQueryData<number | null>(key, Number(markPx));
      };

      if (timeSinceLastUpdate >= THROTTLE_DELAY) {
        doUpdate();
      } else {
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
        }
        throttleTimeoutRef.current = setTimeout(doUpdate, THROTTLE_DELAY - timeSinceLastUpdate);
      }
    },
    [queryClient, coin, key],
  );

  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) clearTimeout(throttleTimeoutRef.current);
    };
  }, []);

  const { isConnected } = useHyperliquidWsConnection({
    id: `markPrice-${coin}`,
    subscription,
    onMessage,
    enabled: isEnabled,
  });

  return { isConnected, markPrice: markPrice ?? null };
};
